import React from 'react';
import { Box, Button, Grid, Typography, Chip, IconButton, Badge } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useState } from 'react';

const planProfiles = [
    {
        id: 1,
        name: 'Basic Discovery',
        type: 'free',
        internalName: 'free_basic',
        ideas: 3,
        depth: 'Basic',
        format: 'Cards',
        model: 'gpt-4o-mini',
        price: 0,
        features: ['AI Roadmap', 'Basic Branding', 'Supplier Discovery'],
        enabledSections: ['branding', 'licensing', 'marketing', 'suppliers', 'market score'],
        isVisible: true,
    },
    {
        id: 2,
        name: 'Premium Insights',
        type: 'premium',
        internalName: 'premium_standard',
        ideas: 5,
        depth: 'Detailed',
        format: 'Tabs',
        model: 'gpt-4o',
        price: 49,
        features: ['Financial Projections', 'Competitor Landscape', 'AI Roadmap', 'Premium Branding', 'Action Plan'],
        enabledSections: ['branding', 'licensing', 'marketing', 'suppliers', 'financials', 'action plan', 'competitors', 'market score'],
        isVisible: true,
    },
    {
        id: 3,
        name: 'Enterprise Suite',
        type: 'enterprise',
        internalName: 'enterprise_comprehensive',
        ideas: 10,
        depth: 'Comprehensive',
        format: 'Report',
        model: 'gpt-4o',
        price: 199,
        features: ['Custom AI Training', 'Expert Advisor', 'Financial Projections', 'Competitor Landscape', 'AI Roadmap', 'Action Plan'],
        enabledSections: ['branding', 'licensing', 'marketing', 'suppliers', 'financials', 'action plan', 'competitors', 'market score'],
        isVisible: true,
    },
    {
        id: 4,
        name: 'Premium Complete',
        type: 'premium',
        internalName: 'premium_complete',
        ideas: 5,
        depth: 'Detailed',
        format: 'Report',
        model: 'gpt-4o',
        price: 99,
        features: ['Full PDF Report', 'Premium Branding', 'Financial Projections', 'Competitor Landscape', 'AI Roadmap', 'Action Plan'],
        enabledSections: ['branding', 'licensing', 'marketing', 'suppliers', 'financials', 'action plan', 'competitors', 'market score'],
        isVisible: true,
    },
    {
        id: 5,
        name: 'Enterprise Custom',
        type: 'enterprise',
        internalName: 'enterprise_custom',
        ideas: 10,
        depth: 'Comprehensive',
        format: 'Dashboard',
        model: 'gpt-4o',
        price: 299,
        features: ['Interactive Dashboard', 'Expert Advisor', 'Custom AI Training', 'Financial Projections', 'Competitor Landscape'],
        enabledSections: ['branding', 'licensing', 'marketing', 'suppliers', 'financials', 'action plan', 'competitors', 'market score'],
        isVisible: true,
    },
];

const getBadgeColor = (type: string) => {
    switch (type) {
        case 'free': return '#64748b';
        case 'premium': return '#f59e0b';
        case 'enterprise': return '#3b82f6';
        default: return '#94a3b8';
    }
};

const FikraLabPlans = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState(planProfiles);

    const handleDeletePlan = (id: number) => {
        setPlans(plans.filter(plan => plan.id !== id));
    };

    const handleToggleVisibility = (id: number) => {
        setPlans(plans.map(plan =>
            plan.id === id ? { ...plan, isVisible: !plan.isVisible } : plan
        ));
    };

    return (
        <div className="fikra_plans_wrapper">
            <div className="main_title d_flex">
                <div className="left_s">
                    <h1>Fikra Lab Plans</h1>
                    <p>Configure what each tier (Free/Premium/Enterprise) can access and generate.</p>
                </div>
                <div className="right_s">
                    <Button
                        className="btn btn_primary"
                        onClick={() => navigate('/fikra-lab-plans/add')}
                        startIcon={<AddIcon />}
                    >
                        Add Plan
                    </Button>
                </div>
            </div>

            <Box sx={{ mt: 4 }}>
                <Grid container spacing={3}>
                    {plans.map((plan) => (
                        <Grid item xs={12} md={6} lg={4} key={plan.id}>
                            <div className={`plan_card ${plan.type} ${!plan.isVisible ? 'hidden_item' : ''}`}>
                                <div className="card_header">
                                    <div className="title_area">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <h3>{plan.name}</h3>
                                            <span className="badge" style={{ backgroundColor: getBadgeColor(plan.type) }}>{plan.type}</span>
                                        </Box>
                                        <p className="internal_name">{plan.internalName}</p>
                                    </div>
                                    <Box display="flex" gap={0.5}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleToggleVisibility(plan.id)}
                                            className="action_btn"
                                        >
                                            {plan.isVisible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                                        </IconButton>
                                        <IconButton
                                            className="edit_btn"
                                            size="small"
                                            onClick={() => navigate('/fikra-lab-plans/add')}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDeletePlan(plan.id)}
                                            className="delete_btn"
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </div>

                                <div className="card_body">
                                    <div className="price_tag">
                                        <label htmlFor={`plan-price-${plan.id}`}>Plan Price</label>
                                        <Typography id={`plan-price-${plan.id}`} variant="h4" className="amount">
                                            {plan.price === 0 ? 'Free' : `$${plan.price}`}
                                            {plan.price > 0 && <span>/mo</span>}
                                        </Typography>
                                    </div>

                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={6}>
                                            <div className="stat_item">
                                                <label htmlFor={`plan-ideas-${plan.id}`}>Ideas Limit</label>
                                                <span id={`plan-ideas-${plan.id}`}>{plan.ideas}</span>
                                            </div>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <div className="stat_item">
                                                <label htmlFor={`plan-depth-${plan.id}`}>Content Depth</label>
                                                <span id={`plan-depth-${plan.id}`}>{plan.depth}</span>
                                            </div>
                                        </Grid>
                                    </Grid>

                                    <div className="features_area">
                                        <label htmlFor={`plan-features-${plan.id}`}>Core Features</label>
                                        <ul id={`plan-features-${plan.id}`} className="feature_mini_list">
                                            {plan.features.slice(0, 4).map((feat, i) => (
                                                <li key={i}>{feat}</li>
                                            ))}
                                            {plan.features.length > 4 && <li>+ {plan.features.length - 4} more</li>}
                                        </ul>
                                    </div>

                                    <div className="sections_area">
                                        <label htmlFor={`plan-sections-${plan.id}`}>Enabled Sections</label>
                                        <div id={`plan-sections-${plan.id}`} className="tags_grid">
                                            {plan.enabledSections.map((section) => (
                                                <Chip key={section} label={section} className="section_tag" size="small" />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="card_decoration">
                                    <AutoAwesomeIcon />
                                </div>
                            </div>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </div>
    );
};

export default FikraLabPlans;
